import { Checkbox } from "primereact/checkbox";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import AudienceUsersList from "@/components/AudienceUsersList";
import type { DirectoryUserShape, StoredUser } from "@/api/assignments";
import type { AvailableUsersMap } from "@/hooks/useAvailableUsers";
import type { MutableRefObject } from "react";
import type { TournamentFormState, TournamentAudienceUser } from "@/api/tournaments";

interface TournamentAudienceProps {
  availableUsers: DirectoryUserShape[];
  allUsers: Map<string, AvailableUsersMap>;
  audience: TournamentFormState["audience"];
  onChangeAudience: (audience: TournamentFormState["audience"]) => void;
  showValidationMessages: boolean;
  validationErrors: { hasError: boolean; message: string };
  isEditMode: boolean;
  selectedUsersToAdd: DirectoryUserShape[];
  setSelectedUsersToAdd: (users: DirectoryUserShape[]) => void;
  selectedUsersToRemove: DirectoryUserShape[];
  setSelectedUsersToRemove: (users: DirectoryUserShape[]) => void;
  onSelectedUsersChange: (users: DirectoryUserShape[]) => void;
  onAddUsersAndContinue: MutableRefObject<(() => void) | null>;
}

/**
 * Maps stored audience users (which may only have email) to full StoredUser objects
 * by looking them up in availableUsers.
 */
const mapAudienceToStoredUsers = (
  audienceUsers: TournamentAudienceUser[],
  availableUsers: DirectoryUserShape[]
): StoredUser[] =>
  audienceUsers
    .map(user => availableUsers.find(au => au.email === user.email))
    .filter((u): u is DirectoryUserShape => u != null)
    .map(u => ({ directoryUuid: u.directoryUuid, email: u.email }));

/**
 * Maps StoredUser objects back to the minimal TournamentAudienceUser format.
 */
const mapStoredUsersToAudience = (
  formUsers: StoredUser[],
  availableUsers: DirectoryUserShape[]
): TournamentAudienceUser[] =>
  formUsers
    .map(u => ({
      directoryUuid: u.directoryUuid,
      email: availableUsers.find(au => au.directoryUuid === u.directoryUuid)?.email,
    }))
    .filter(u => u.email);

export function TournamentAudience({
  availableUsers,
  allUsers,
  audience,
  onChangeAudience,
  showValidationMessages,
  validationErrors,
  isEditMode,
  selectedUsersToAdd,
  setSelectedUsersToAdd,
  selectedUsersToRemove,
  setSelectedUsersToRemove,
  onSelectedUsersChange,
  onAddUsersAndContinue,
}: TournamentAudienceProps) {
  const storedUsers = mapAudienceToStoredUsers(audience.users, availableUsers);

  const handleUsersChange = (values: {
    users: StoredUser[];
    availableUsers: DirectoryUserShape[];
  }) => {
    onChangeAudience({
      ...audience,
      users: mapStoredUsersToAudience(values.users, availableUsers),
    });
  };

  return (
    <div className="p-4">
      <div className="flex items-center">
        <Checkbox
          inputId="isGlobalTournament"
          checked={audience.isGlobal}
          onChange={e =>
            onChangeAudience({
              ...audience,
              isGlobal: !!e.checked,
              users: e.checked && !isEditMode ? [] : audience.users,
            })
          }
        />
        <label htmlFor="isGlobalTournament" className="ml-2">
          Make this tournament available to everyone
        </label>
      </div>
      <fieldset className="mt-4" disabled={audience.isGlobal}>
        <AudienceUsersList
          key={isEditMode ? String(storedUsers.length > 0) : undefined}
          hasPrepopulatedUsers={false}
          values={{ users: storedUsers, availableUsers, allUsers }}
          handleChange={handleUsersChange}
          showNewHire={false}
          onSelectedUsersChange={onSelectedUsersChange}
          onAddUsersAndContinue={onAddUsersAndContinue}
          selectedUsersToAdd={selectedUsersToAdd}
          setSelectedUsersToAdd={setSelectedUsersToAdd}
          selectedUsersToRemove={selectedUsersToRemove}
          setSelectedUsersToRemove={setSelectedUsersToRemove}
          entityType="Tournament"
        />
      </fieldset>
      {showValidationMessages && validationErrors.hasError && (
        <ValidationMessage severity="error" text={validationErrors.message} />
      )}
    </div>
  );
}
